/**
 * User Service — aligned with AUTH_V2_REACT_FRONTEND_DOCS.md
 *
 * Handles: user info, user updates, profile CRUD,
 * account activation/deactivation.
 */
import axiosInstance from "@/config/axiosInstance";
import { getAuthApiRoute } from "@/helpers/api.util";

const getTemporaryAuthConfig = (accessToken) =>
    accessToken
        ? {
            headers: { Authorization: `Bearer ${accessToken}` },
            skipAuthRefresh: true,
        }
        : undefined;

const ALLOWED_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
]);
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const validateImage = (image) => {
    if (!image) return;
    if (!ALLOWED_IMAGE_TYPES.has(image.type)) {
        throw new Error("فرمت تصویر باید JPEG، PNG یا WebP باشد");
    }
    if (image.size > MAX_IMAGE_SIZE_BYTES) {
        throw new Error("حجم تصویر نباید بیشتر از ۱۰ مگابایت باشد");
    }
};

// ---------------------------------------------------------------------------
// User Info
// ---------------------------------------------------------------------------

/**
 * Get the authenticated user's info (profiles, phone, etc.).
 *
 * GET /user  (Bearer)
 * @returns {User}
 */
export const getUser = async ({ accessToken } = {}) => {
    const { data } = await axiosInstance.get(
        getAuthApiRoute("user"),
        getTemporaryAuthConfig(accessToken)
    );
    return data.data;
};

/**
 * Get user info using OTP headers (pre-login, after OTP verification).
 *
 * GET /user  (x-mobile-number + x-otp headers)
 *
 * @param {number} mobileNumber
 * @param {number} otp
 * @returns {User}
 */
export const getUserWithOTP = async (mobileNumber, otp) => {
    const { data } = await axiosInstance.get(getAuthApiRoute("user"), {
        headers: {
            "x-mobile-number": mobileNumber.toString(),
            "x-otp": otp.toString(),
        },
        skipAuth: true,
        skipAuthRefresh: true,
    });
    return data.data;
};

// ---------------------------------------------------------------------------
// Update User
// ---------------------------------------------------------------------------

/**
 * Update the authenticated user's info.
 * Supports image upload via multipart/form-data.
 *
 * PATCH /user  (Bearer)
 *
 * @param {{ firstName?: string, lastName?: string, ageRange?: 7|13|18|120, city?: string, state?: string, email?: string, password?: string, image?: File }} params
 */
export const updateUser = async (params) => {
    validateImage(params.image);
    const formData = new FormData();
    Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined) {
            formData.append(key, val);
        }
    });

    const { data } = await axiosInstance.patch(
        getAuthApiRoute("user"),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data.data;
};

// ---------------------------------------------------------------------------
// Profile CRUD
// ---------------------------------------------------------------------------

/**
 * Add a new profile (max 3 per user).
 *
 * POST /profile  (Bearer)
 *
 * @param {{ fullName: string, ageRange: 7|13|18|120, image?: File }} params
 */
export const addProfile = async (params, { accessToken } = {}) => {
    validateImage(params.image);
    const formData = new FormData();
    formData.append("fullName", params.fullName);
    formData.append("ageRange", String(params.ageRange));
    if (params.image) formData.append("image", params.image);

    const { data } = await axiosInstance.post(
        getAuthApiRoute("profile"),
        formData,
        {
            ...getTemporaryAuthConfig(accessToken),
            headers: {
                ...getTemporaryAuthConfig(accessToken)?.headers,
                "Content-Type": "multipart/form-data",
            },
        }
    );
    return data.data;
};

/**
 * Update an existing profile.
 *
 * PATCH /profile  (Bearer)
 *
 * @param {{ profileId: string, fullName?: string, image?: File }} params
 */
export const updateProfile = async (params) => {
    validateImage(params.image);
    const formData = new FormData();
    formData.append("profileId", params.profileId);
    if (params.fullName) formData.append("fullName", params.fullName);
    if (params.image) formData.append("image", params.image);

    const { data } = await axiosInstance.patch(
        getAuthApiRoute("profile"),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data.data;
};

/**
 * Delete a profile. Cannot delete the last remaining profile.
 *
 * DELETE /profile/:profileId  (Bearer)
 *
 * @param {string} profileId
 */
export const deleteProfile = async (profileId, { accessToken } = {}) => {
    const { data } = await axiosInstance.delete(
        getAuthApiRoute(`profile/${profileId}`),
        getTemporaryAuthConfig(accessToken)
    );
    return data.data;
};

// ---------------------------------------------------------------------------
// Account Activation / Deactivation
// ---------------------------------------------------------------------------

/**
 * Deactivate the authenticated user's account.
 *
 * PATCH /user/deactive  (Bearer)
 */
export const deactivateAccount = async () => {
    const { data } = await axiosInstance.patch(getAuthApiRoute("user/deactive"));
    return data.data;
};

/**
 * Reactivate a deactivated account using OTP verification.
 *
 * PATCH /user/active
 *
 * @param {number} mobileNumber
 * @param {number} otp
 */
export const activateAccount = async (mobileNumber, otp) => {
    const { data } = await axiosInstance.patch(
        getAuthApiRoute("user/active"),
        { mobileNumber, otp },
        { skipAuth: true, skipAuthRefresh: true }
    );
    return data.data;
};
